import React, { memo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Col, Row } from 'reactstrap';
import { SOURCES } from 'client/utils/image-helpers';
import { getNoVehicleImageToDisplay } from 'site-modules/shared/utils/get-no-vehicle-image-to-display';
import { useToggle } from 'site-modules/shared/hooks/use-toggle';
import { formatPriceStringV2, getPriceStringV2 } from 'site-modules/shared/utils/price-utils';
import { Image } from 'site-modules/shared/components/image/image';
import { LeaseDealsCta } from 'site-modules/lease-deals/components/lease-deals-cta/lease-deals-cta';
import { LeaseDealsDetailsModal } from 'site-modules/lease-deals/components/lease-deals-details-modal/lease-deals-details-modal';

export const PreviewCard = memo(function PreviewCard({ deal, isMobile = false }) {
  const [isModalOpen, toggleModal] = useToggle(false);

  const { photo, year, make, model, payment, dueAtSigning, msrp, term, styleId } = deal;
  const errorImage = getNoVehicleImageToDisplay(deal.bodyType);

  const signingPrice = getPriceStringV2(dueAtSigning, 'Contact dealer', { allowZero: true });
  const formattedMsrp = getPriceStringV2(msrp);
  const vehicleName = `${year} ${make} ${model}`;

  return (
    <>
      <div id="lease-deals-preview-card" className="preview-card bg-white rounded-24 pos-r p-1 py-1_5 py-md-1 mx-auto">
        <button
          type="button"
          className="pos-a top-0 left-0 w-100 h-100 border-0 bg-transparent"
          onClick={toggleModal}
          data-tracking-id="view_details"
          data-tracking-value={styleId}
          aria-label={`View full details: ${vehicleName}`}
        />

        <Row className="gx-2 align-items-center">
          <Col xs={12} md={5} className="mb-2 mb-md-0">
            <Image
              lazy
              alt={photo?.title || vehicleName}
              source={SOURCES.MEDIA}
              errorImage={errorImage}
              imageId={photo?.href ?? ''}
              className="vehicle-image object-fit-cover w-100 rounded-20"
            />
          </Col>
          <Col xs={12} md={7} className="preview-card-details">
            <h2 className="size-20 mb-0 text-center text-md-start">Selected Deal</h2>
            <div className="d-flex flex-column gap-md-1">
              <h3 className="preview-title text-cool-gray-10 size-24 fw-bold mb-1 mb-md-0 text-center text-md-start">
                {vehicleName}
              </h3>
              <p className="payment-value mb-1 mb-md-0 fw-bold d-flex align-items-center justify-content-center justify-content-md-start gap-0_5">
                {formatPriceStringV2(payment)}
                <small className="text-muted size-14 fw-medium pt-0_5">/mo*</small>
              </p>
              <p className="preview-meta mb-1 mb-md-0 text-cool-gray-40 mt-0 size-16 d-flex align-items-center flex-wrap column-gap-0_75 column-gap-md-1 row-gap-0_25 justify-content-center justify-content-md-start">
                <span>
                  <strong className="text-cool-gray-30">{signingPrice}</strong> due at signing
                </span>
                <span aria-hidden="true">|</span>
                <span>
                  <strong className="text-cool-gray-30">{term} months</strong> term
                </span>
                {!isMobile && <span aria-hidden="true">|</span>}
                <span className={classNames({ 'w-100 text-center': isMobile })}>
                  MSRP <strong className="text-cool-gray-30">{formattedMsrp}</strong>
                </span>
              </p>
            </div>
            <div className="preview-actions mt-1 d-flex flex-wrap gap-0_5 justify-content-center justify-content-md-start pos-r z-1">
              <LeaseDealsCta
                {...deal}
                photo={photo}
                errorImage={errorImage}
                showCta
                buttonProps={{
                  className: 'btn-blue-50 pos-r text-transform-none fw-medium py-1 px-1_5 rounded-pill fw-medium',
                }}
                formName="selected-lease-deal-preview"
              />
              <button
                type="button"
                className="btn btn-outline-blue-50 text-transform-none fw-medium py-1 px-1_5 rounded-pill fw-medium size-16"
                onClick={toggleModal}
                data-tracking-id="view_details_button"
                data-tracking-value={styleId}
                aria-label={`View lease details for ${vehicleName}`}
              >
                View lease details
              </button>
            </div>
          </Col>
        </Row>
      </div>

      <LeaseDealsDetailsModal
        {...deal}
        photo={photo}
        errorImage={errorImage}
        isOpen={isModalOpen}
        onClose={toggleModal}
        isMobile={isMobile}
        showCta
      />
    </>
  );
});

PreviewCard.propTypes = {
  deal: PropTypes.shape({
    id: PropTypes.string.isRequired,
    year: PropTypes.number.isRequired,
    make: PropTypes.string.isRequired,
    model: PropTypes.string.isRequired,
    trim: PropTypes.string,
    bodyType: PropTypes.string,
    styleId: PropTypes.number.isRequired,
    payment: PropTypes.number.isRequired,
    dueAtSigning: PropTypes.number.isRequired,
    msrp: PropTypes.number.isRequired,
    term: PropTypes.number.isRequired,
    endDate: PropTypes.string.isRequired,
    disclaimer: PropTypes.string,
    webUrls: PropTypes.string,
    photo: PropTypes.shape({ href: PropTypes.string, title: PropTypes.string }),
  }).isRequired,
  isMobile: PropTypes.bool,
};
